import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaCloudUploadAlt,
  FaEdit,
  FaHistory,
  FaLeaf,
  FaPlus,
  FaTractor,
} from "react-icons/fa";
import { toast } from "react-toastify";

import myConfig from "../../configs/config";
import CreateScheduleModal from "../PendingSchedules/CreateScheduleModal";
import { DEFAULT_CALIBRATION_TOLERANCE_PERCENT } from "../PendingSchedules/serviceOrderProducts";
import { serviceOrderTypeLabel } from "../../constants/serviceOrderTypes";
import {
  ordersHaveUnpublishedChanges,
  publishPendingServiceOrders,
} from "../../utils/serviceOrderSyncApi";
import "./FieldAppCalibrationWorkSection.css";

function planMachineIdent(plan) {
  if (!plan || !plan.machine_ident) return "";
  return String(plan.machine_ident).trim();
}

function planTargetRate(plan) {
  if (!plan) return null;
  const rate = Number(plan.target_rate_l_ha || plan.spray_volume_per_ha);
  return Number.isFinite(rate) && rate > 0 ? rate : null;
}

function planTolerance(plan) {
  if (!plan) return DEFAULT_CALIBRATION_TOLERANCE_PERCENT;
  const value = Number(plan.calibration_tolerance_percent);
  return Number.isFinite(value) && value > 0
    ? value
    : DEFAULT_CALIBRATION_TOLERANCE_PERCENT;
}

function formatOrderDateTime(raw) {
  if (!raw) return "";
  return new Date(raw).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatHistoryTarget(plan) {
  const rate = planTargetRate(plan);
  return rate != null ? `${rate.toFixed(2)} L/ha` : "—";
}

function ServiceOrderHistory({ history, t }) {
  const [open, setOpen] = useState(false);
  if (!Array.isArray(history) || history.length === 0) return null;

  return (
    <div className="field-app-calibration-work__history">
      <button
        type="button"
        className="field-app-calibration-work__history-toggle"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        <FaHistory style={{ marginRight: 6 }} />
        {t("Receitas anteriores")} ({history.length})
        {open ? (
          <FaChevronUp style={{ marginLeft: 6 }} />
        ) : (
          <FaChevronDown style={{ marginLeft: 6 }} />
        )}
      </button>
      {open ? (
        <ul className="field-app-calibration-work__history-list">
          {[...history].reverse().map((entry, index) => (
            <li key={`${entry.revision}-${index}`}>
              <strong>
                {t("Revisão")} {entry.revision || "—"}
              </strong>
              {entry.published_at ? (
                <span>
                  {t("Publicada em")}: {formatOrderDateTime(entry.published_at)}
                </span>
              ) : null}
              <span>
                {t("Taxa-alvo")}: {formatHistoryTarget(entry.plan)}
              </span>
              {entry.executed_start_date ? (
                <span>{t("Execução iniciada")}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default function FieldAppCalibrationWorkSection({
  farmId,
  t,
  onUnpublishedStatusChange,
  onRegisterPublishHandler,
}) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("client-token")}`,
    }),
    []
  );

  const hasUnpublished = useMemo(
    () => ordersHaveUnpublishedChanges(orders),
    [orders]
  );

  useEffect(() => {
    if (onUnpublishedStatusChange) {
      onUnpublishedStatusChange(hasUnpublished);
    }
  }, [hasUnpublished, onUnpublishedStatusChange]);

  const loadOrders = useCallback(async () => {
    if (!farmId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${myConfig.API_URL}/observation/pending/`, {
        params: { farm_id: farmId },
        headers: authHeaders,
      });
      const items = Array.isArray(response.data) ? response.data : [];
      setOrders(
        items
          .filter((item) => !item.confirmed)
          .sort((a, b) => {
            const dateA = new Date(a.created || a.start_date).getTime();
            const dateB = new Date(b.created || b.start_date).getTime();
            return dateB - dateA;
          })
      );
    } catch (error) {
      toast.error(t("Erro ao carregar ordens de serviço."), {
        position: "bottom-right",
      });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, farmId, t]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handlePublish = useCallback(async () => {
    if (!farmId) return;
    setPublishing(true);
    try {
      const result = await publishPendingServiceOrders(farmId);
      await loadOrders();
      toast.success(
        result.detail ||
          t("Ordens sincronizadas com o app de campo."),
        { position: "bottom-right", autoClose: 8000 }
      );
    } catch (error) {
      toast.error(t("Não foi possível sincronizar com o app de campo."), {
        position: "bottom-right",
      });
      throw error;
    } finally {
      setPublishing(false);
    }
  }, [farmId, loadOrders, t]);

  useEffect(() => {
    if (onRegisterPublishHandler) {
      onRegisterPublishHandler(handlePublish);
    }
  }, [handlePublish, onRegisterPublishHandler]);

  function openCreateModal() {
    setEditingOrder(null);
    setShowModal(true);
  }

  function openEditModal(order) {
    setEditingOrder(order);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingOrder(null);
  }

  async function handleSaved() {
    await loadOrders();
  }

  return (
    <section className="field-app-calibration-work">
      <div className="field-app-calibration-work__sync-bar">
        <button
          type="button"
          className={
            hasUnpublished
              ? "btn btn-primary btn-sm field-app-calibration-work__sync-btn field-app-calibration-work__sync-btn--pending"
              : "btn btn-primary btn-sm field-app-calibration-work__sync-btn"
          }
          onClick={handlePublish}
          disabled={loading || publishing || !hasUnpublished}
        >
          <FaCloudUploadAlt style={{ marginRight: 6 }} />
          {publishing
            ? t("Sincronizando...")
            : t("Sincronizar com app de campo")}
        </button>
        {hasUnpublished ? (
          <span className="field-app-calibration-work__sync-badge">
            {t("Alterações não sincronizadas")}
          </span>
        ) : null}
        <span className="field-app-calibration-work__sync-hint">
          {t(
            "Edições ficam salvas na plataforma; a receita anterior permanece no histórico até você sincronizar a nova versão."
          )}
        </span>
      </div>

      <div className="field-app-calibration-work__header">
        <div>
          <h3>
            <FaLeaf style={{ marginRight: 8 }} />
            {t("Trabalhos para o check de calibragem")}
          </h3>
          <p>
            {t(
              "Crie ou edite ordens de serviço. Ao sincronizar, a nova dosagem substitui a anterior no app, mas as receitas enviadas permanecem registradas aqui."
            )}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-success btn-sm"
          onClick={openCreateModal}
        >
          <FaPlus style={{ marginRight: 6 }} />
          {t("Nova ordem de calibragem")}
        </button>
      </div>

      {loading ? (
        <p>{t("Carregando ordens...")}</p>
      ) : orders.length === 0 ? (
        <p className="field-app-calibration-work__empty">
          {t(
            "Nenhuma ordem pendente. Crie uma ordem com pulverização ou aplicação, informe a vazão L/ha e escolha a máquina."
          )}
        </p>
      ) : (
        <ul className="field-app-calibration-work__list">
          {orders.map((order) => {
            const plan = order.service_order_plan || {};
            const publishedPlan = order.service_order_plan_published || plan;
            const machine = planMachineIdent(plan);
            const targetRate = planTargetRate(plan);
            const publishedRate = planTargetRate(publishedPlan);
            const tolerance = planTolerance(plan);
            const createdAt = formatOrderDateTime(order.created);
            const history = order.service_order_plan_history || [];
            const isDraft = Boolean(order.has_unpublished_changes);

            return (
              <li key={order.id}>
                <button
                  type="button"
                  className={
                    isDraft
                      ? "field-app-calibration-work__item field-app-calibration-work__item--draft"
                      : "field-app-calibration-work__item"
                  }
                  onClick={() => openEditModal(order)}
                >
                  <div className="field-app-calibration-work__item-top">
                    <div className="field-app-calibration-work__item-title">
                      {serviceOrderTypeLabel(order.service_type, t) ||
                        t("Ordem de serviço")}
                      {order.description
                        ? ` — ${order.description.split("\n")[0]}`
                        : ""}
                      {isDraft ? (
                        <span className="field-app-calibration-work__draft-badge">
                          {t("Rascunho")}
                        </span>
                      ) : null}
                    </div>
                    <span className="field-app-calibration-work__edit-hint">
                      <FaEdit style={{ marginRight: 4 }} />
                      {t("Editar")}
                    </span>
                  </div>
                  <div className="field-app-calibration-work__item-meta">
                    {createdAt ? (
                      <span>
                        <FaCalendarAlt style={{ marginRight: 4 }} />
                        {t("Criada em")}: {createdAt}
                      </span>
                    ) : null}
                    {machine ? (
                      <span>
                        <FaTractor style={{ marginRight: 4 }} />
                        {machine}
                      </span>
                    ) : (
                      <span>{t("Máquina não especificada")}</span>
                    )}
                    {isDraft && publishedRate != null ? (
                      <span>
                        {t("No app")}: {publishedRate.toFixed(2)} L/ha
                      </span>
                    ) : null}
                    {targetRate != null ? (
                      <span>
                        {isDraft ? t("Rascunho") : t("Taxa-alvo")}:{" "}
                        {targetRate.toFixed(2)} L/ha
                      </span>
                    ) : null}
                    <span>
                      {t("Tolerância")}: ±{tolerance.toFixed(1)}%
                    </span>
                    {order.service_order_sync_revision > 0 ? (
                      <span>
                        {t("Revisão")}: {order.service_order_sync_revision}
                      </span>
                    ) : null}
                    {Array.isArray(order.assigned_operators) &&
                    order.assigned_operators.length > 0 ? (
                      <span>
                        {t("Operadores")}:{" "}
                        {order.assigned_operators.map((op) => op.name).join(", ")}
                      </span>
                    ) : null}
                  </div>
                </button>
                <ServiceOrderHistory history={history} t={t} />
              </li>
            );
          })}
        </ul>
      )}

      <CreateScheduleModal
        show={showModal}
        onHide={closeModal}
        farmId={farmId}
        onCreated={handleSaved}
        defaultServiceType="pulverizacao"
        requireMachine
        editingOrder={editingOrder}
      />
    </section>
  );
}
